import React, { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from 'react'
import { Box, IconButton, TablePagination } from '@mui/material'
import {
  ArrowForward,
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LastPage
} from '@mui/icons-material'
import styled from 'styled-components'
import { StyledInput } from 'Components/Inputs/StyledInput'

const ActionsContainer = styled(Box)`
  display: flex;
  align-items: center;
  margin-left: 20px;
`

const GoToPageContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
`

const PageInput = styled(StyledInput)`
  min-width: 80px;
  height: 32px;
  padding: 0 8px;
  font-size: 14px;
`

interface CustomTablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void
  withDirectNavigation?: boolean
}

const CustomTablePaginationActions: FC<CustomTablePaginationActionsProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  withDirectNavigation = false
}) => {
  const [goToPageValue, setGoToPageValue] = useState('')
  const totalPages = Math.ceil(count / rowsPerPage)

  const handleFirstPageButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, 0)
    },
    [onPageChange]
  )
  const handleBackButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page - 1)
    },
    [page, onPageChange]
  )
  const handleNextButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, page + 1)
    },
    [page, onPageChange]
  )
  const handleLastPageButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
    },
    [count, rowsPerPage, onPageChange]
  )

  const handleGoToPage = useCallback(() => {
    const pageNumber = parseInt(goToPageValue, 10)
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(null, pageNumber - 1)
      setGoToPageValue('')
    }
  }, [goToPageValue, totalPages, onPageChange])

  const handleGoToPageConfirm = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleGoToPage()
      }
    },
    [handleGoToPage]
  )

  return (
    <ActionsContainer>
      {withDirectNavigation && (
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          <FirstPage />
        </IconButton>
      )}
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      {withDirectNavigation && (
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          <LastPage />
        </IconButton>
      )}
      {withDirectNavigation && totalPages > 1 && (
        <GoToPageContainer>
          <span style={{ textWrap: 'nowrap' }}>Go to:</span>
          <PageInput
            type="number"
            value={goToPageValue}
            onChange={(e) => setGoToPageValue(e.target.value)}
            onKeyDown={handleGoToPageConfirm}
            placeholder={`1-${totalPages}`}
            min={1}
            max={totalPages}
          />
          <IconButton
            size="small"
            onClick={handleGoToPage}
            disabled={
              !goToPageValue ||
              parseInt(goToPageValue, 10) < 1 ||
              parseInt(goToPageValue, 10) > totalPages
            }
            aria-label="go to page"
          >
            <ArrowForward fontSize="small" />
          </IconButton>
        </GoToPageContainer>
      )}
    </ActionsContainer>
  )
}

type PaginationProps = {
  optionText: string
  totalRows: number
  rowsPerPage: number
  page: number
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void
  handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void
  withDirectNavigation?: boolean
}
export const Pagination: FC<PaginationProps> = ({
  optionText,
  totalRows,
  rowsPerPage,
  page,
  handleChangeRowsPerPage,
  handleChangePage,
  withDirectNavigation = false
}) => (
  <TablePagination
    rowsPerPageOptions={[
      { value: 10, label: `10 ${optionText}` },
      { value: 25, label: `25 ${optionText}` },
      { value: 50, label: `50 ${optionText}` }
    ]}
    component="div"
    labelRowsPerPage={''}
    labelDisplayedRows={({ from, to, count }) =>
      `Showing ${from} to ${to} of ${count} ${optionText}`
    }
    count={totalRows}
    rowsPerPage={rowsPerPage}
    page={totalRows === 0 ? 0 : page}
    onPageChange={handleChangePage}
    onRowsPerPageChange={handleChangeRowsPerPage}
    ActionsComponent={({ count, page, rowsPerPage, onPageChange }) => (
      <CustomTablePaginationActions
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        withDirectNavigation={withDirectNavigation}
      />
    )}
  />
)
